/**
 * Markup and style fragments shared by the paged (`render-export-html.ts`) and
 * the continuous (`render-export-html-continuous.ts`) HTML documents.
 *
 * Internal to `export-render`: deliberately **not** re-exported from
 * `index.ts`. These are the renderer's building blocks, not a public API — the
 * public surface stays `renderExportHtml` and its `layout` switch, so the two
 * layouts can keep sharing (and changing) their pieces without any consumer
 * depending on a fragment's exact shape.
 *
 * Every fragment here must keep emitting exactly what the paged renderer
 * emitted before the split (issue #12): the paged golden master
 * (`tests/render-export-html-paged-golden.test.ts`) pins that byte for byte.
 */
import type { ExportAppearanceOptions } from '../schemas/export.js';
import type { Noyau } from '../schemas/noyau.js';
import {
  ACTIVATION_GROUP_LABEL,
  type ExportModel,
  type ExportModelColumn,
  type ExportModelRow,
} from './build-export-model.js';
import {
  HEADER_LOGO_HEIGHT_PX,
  HEADER_MAX_TITLE_LINES,
  HEADER_META_PT,
  HEADER_TITLE_PT,
  HEADER_VERTICAL_PADDING_PX,
} from './document-header.js';
import { headerCellStyleAttribute, headerTextStyleAttribute } from './header-theme-style.js';
import {
  DEFAULT_COLUMN_WIDTH_PX,
  LINE_HEIGHT_FACTOR,
  ROW_LABEL_WIDTH_PX,
} from './page-geometry.js';
import type { BulletLine } from './parse-bullet-lines.js';
import type { RenderExportHtmlInput } from './render-export-html.js';
import type { TransposedSection } from './transposed.js';

/** Spec R5 — the two border treatments, as literals rather than a computed weight/colour pair. */
export const BORDER_STYLE = {
  fines: '1px solid #d9d6d2',
  marquees: '1.5px solid #b3aea8',
} as const;
export const BORDER_COLOR = { fines: '#d9d6d2', marquees: '#b3aea8' } as const;

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Trims float noise off a computed CSS length (12pt × 96/72 must read `16px`, not `16.000000000000004px`). */
export function formatPx(value: number): string {
  return String(Number(value.toFixed(2)));
}

export function renderSubjectDescriptionBlock(
  subjectDescription: RenderExportHtmlInput['subjectDescription'],
): string {
  if (!subjectDescription || !subjectDescription.include) return '';
  const parts = [
    subjectDescription.sujet
      ? `<p class="export-subject">${escapeHtml(subjectDescription.sujet)}</p>`
      : '',
    subjectDescription.contexte
      ? `<p class="export-description">${escapeHtml(subjectDescription.contexte)}</p>`
      : '',
  ].filter((part) => part.length > 0);
  if (parts.length === 0) return '';
  return `<section class="export-subject-description" style="margin-bottom:16px">${parts.join('')}</section>`;
}

/**
 * One `<th>` in a tile's label column(s). When `showRowLabels` is false the
 * cell still renders — with the *same* rowspan/colspan — but visually hidden:
 * dropping it (or its span attributes) would shift every following cell left
 * and break this tile's alignment with its siblings.
 */
function labelCell(
  showRowLabels: boolean,
  className: string,
  spanAttributes: string,
  content: string,
): string {
  return showRowLabels
    ? `<th scope="row" class="${className}"${spanAttributes}>${escapeHtml(content)}</th>`
    : `<th scope="row" class="${className} export-row-label-hidden"${spanAttributes}></th>`;
}

/**
 * The `<colgroup>` of a grid table. `table-layout: fixed` derives column
 * widths from whichever row renders first in the table. When a tile shows no
 * column headers, the <thead> is empty, so relying on <th style="width:...px">
 * alone would let the browser fall back to equal-width columns for the
 * first-rendered (body) row. A <colgroup> makes column widths authoritative
 * regardless of which row renders first, so it — not individual header/label
 * cells — owns sizing.
 */
export function renderGridColgroup(
  columns: readonly ExportModelColumn[],
  columnWidths: Record<string, number>,
  labelColumnCount: 1 | 2,
  scale: number,
): string {
  const rowLabelWidthPx = Math.round(ROW_LABEL_WIDTH_PX * scale);
  const labelCols = `<col style="width:${rowLabelWidthPx}px" />`.repeat(labelColumnCount);
  return `<colgroup>${labelCols}${columns
    .map((column) => {
      const widthPx = Math.round(
        (columnWidths[String(column.number)] ?? DEFAULT_COLUMN_WIDTH_PX) * scale,
      );
      return `<col style="width:${widthPx}px" />`;
    })
    .join('')}</colgroup>`;
}

/**
 * The grid's column-header row: one empty corner cell per label column, then
 * one header per palier, carrying the palier label alone (issue #16: no palier
 * number in any header). The number survives as `data-palier-number`, a
 * technical id.
 *
 * Decision F3 (now spec R5): a painted theme puts its colours on each header
 * as literal hex, inline. Inline rather than a class per palier because the
 * palette is keyed by a *number* that can be negative (-10), which makes for
 * awkward class names, and because Playwright never loads the app's
 * stylesheet — see `headerCellStyleAttribute`. The text colour goes on the
 * spans, not inherited from the <th> — see `headerTextStyleAttribute`.
 */
export function renderColumnHeaderRow(
  columns: readonly ExportModelColumn[],
  labelColumnCount: 1 | 2,
  appearance: ExportAppearanceOptions,
  noyau: Noyau | null,
): string {
  const theme = appearance.headerTheme;
  return `<tr>${'<th class="export-corner"></th>'.repeat(labelColumnCount)}${columns
    .map((column) => {
      const cellStyle = headerCellStyleAttribute(theme, column.number, noyau);
      const textStyle = headerTextStyleAttribute(theme, column.number, noyau);
      return `<th class="export-col-header"${cellStyle} data-palier-number="${column.number}"><span class="export-col-label"${textStyle}>${escapeHtml(column.label)}</span></th>`;
    })
    .join('')}</tr>`;
}

/**
 * The label cell(s) opening one grid body row.
 *
 * The six-row activation block is labelled by a single spanning group cell
 * (unless `inline`), emitted on the first activation row present and spanning
 * only the `activationRowCount` activation rows actually present in this
 * table — a paged tile can carry just part of the block.
 */
export function renderRowLabelCells(input: {
  row: ExportModelRow;
  isFirstActivationRow: boolean;
  activationRowCount: number;
  activationLevierNames: RenderExportHtmlInput['activationLevierNames'];
  labelColumnCount: 1 | 2;
  showRowLabels: boolean;
}): string {
  const { row, activationLevierNames, labelColumnCount, showRowLabels } = input;
  if (row.kind !== 'activation') {
    return labelCell(
      showRowLabels,
      'export-row-label',
      labelColumnCount === 2 ? ' colspan="2"' : '',
      row.label,
    );
  }
  if (activationLevierNames === 'inline') {
    // No group label and no span — each levier row owns its label cell.
    return labelCell(showRowLabels, 'export-row-label', '', row.label);
  }
  const groupCell = input.isFirstActivationRow
    ? labelCell(
        showRowLabels,
        'export-row-label',
        ` rowspan="${input.activationRowCount}"`,
        ACTIVATION_GROUP_LABEL,
      )
    : '';
  const levierCell =
    activationLevierNames === 'column'
      ? labelCell(showRowLabels, 'export-levier-label', '', row.label)
      : '';
  return `${groupCell}${levierCell}`;
}

/**
 * One cell's bullet lines, or the « Aucun contenu » placeholder.
 *
 * Decision A3: a highlight is (cell, line index), so it needs no coordinates
 * and survives every layout change — paged or continuous alike.
 *
 * Decision A4: the anchor identity travels in the markup so the annotation
 * overlay can hit-test by element rather than by pixel geometry or by
 * matching line text — the fragility that ruled out annotating the pdf.js
 * canvas. Inert in the PDF: Chromium ignores unknown data-* attributes.
 */
export function renderCellLines(
  lines: readonly BulletLine[],
  rowKey: string,
  columnNumber: number,
  highlightKeys: ReadonlySet<string>,
): string {
  if (lines.length === 0) return '<span class="export-empty">Aucun contenu</span>';
  return lines
    .map((line, lineIndex) => {
      const highlighted = highlightKeys.has(`${rowKey}:${columnNumber}:${lineIndex}`);
      const className = highlighted ? ' class="export-line-highlight"' : '';
      const anchor = ` data-line-index="${lineIndex}"`;
      return line.hasBullet
        ? `<li${className}${anchor}>${escapeHtml(line.text)}</li>`
        : `<p${className}${anchor}>${escapeHtml(line.text)}</p>`;
    })
    .join('');
}

/** One grid body row's data cells, one per palier column. */
export function renderDataCells(
  model: ExportModel,
  row: ExportModelRow,
  columns: readonly ExportModelColumn[],
  highlightKeys: ReadonlySet<string>,
): string {
  return columns
    .map((column) => {
      const lines = model.cellContent[row.key]?.[column.number] ?? [];
      const content = renderCellLines(lines, row.key, column.number, highlightKeys);
      return `<td data-row-key="${escapeHtml(row.key)}" data-column-number="${column.number}">${content}</td>`;
    })
    .join('');
}

/**
 * One `<table class="export-transposed">` per section (spec R2/P2 Dossier): a
 * full-width title row, then one row per palier — the palier's number+label as
 * the row header, and this section's cell lines beside it. Same li/p markup
 * and `data-*` anchors as the grid table (decision A3/A4), so line highlights
 * still work; `row.rowKey` is the underlying dimension row key, or the
 * synthetic activation-group key (`transposed.ts`) for the activation section.
 *
 * `titlePlacement: 'head'` moves the title row into a `<thead>` — the
 * continuous document makes it sticky, so a section's title stays in view
 * while that section scrolls past. The paged document keeps it in the body.
 */
export function renderTransposedSectionHtml(
  section: TransposedSection,
  appearance: ExportAppearanceOptions,
  noyau: Noyau | null,
  highlightKeys: ReadonlySet<string>,
  titlePlacement: 'body' | 'head',
): string {
  const titleRow = `<tr><th scope="colgroup" class="export-transposed-title" colspan="2">${escapeHtml(section.title)}</th></tr>`;

  const bodyRows = section.rows
    .map((row) => {
      // Same painted-theme treatment as the grid's column headers (spec R5,
      // issue #8) — here the palier identity lives on the row label instead
      // of a column.
      const themeStyle = headerCellStyleAttribute(appearance.headerTheme, row.palier.number, noyau);
      const textStyle = headerTextStyleAttribute(appearance.headerTheme, row.palier.number, noyau);
      const content = renderCellLines(row.lines, row.rowKey, row.palier.number, highlightKeys);
      return `<tr><th scope="row" class="export-transposed-palier"${themeStyle}><span class="export-col-label"${textStyle}>${escapeHtml(row.palier.label)}</span></th><td data-row-key="${escapeHtml(row.rowKey)}" data-column-number="${row.palier.number}">${content}</td></tr>`;
    })
    .join('');

  const colgroup = `<colgroup><col style="width:${ROW_LABEL_WIDTH_PX}px" /><col /></colgroup>`;
  return titlePlacement === 'head'
    ? `<table class="export-transposed">${colgroup}<thead>${titleRow}</thead><tbody>${bodyRows}</tbody></table>`
    : `<table class="export-transposed">${colgroup}<tbody>${titleRow}${bodyRows}</tbody></table>`;
}

export function pmsSignatureHtml(input: RenderExportHtmlInput): string {
  const { pmsSignature } = input.dressing;
  if (pmsSignature === 'aucune') return '';
  const horizontal =
    input.pmsLogoHorizontalDataUri === null
      ? ''
      : `<img src="${escapeHtml(input.pmsLogoHorizontalDataUri)}" alt="patrickmathieu singularité" />`;
  const ring =
    input.pmsLogoRingDataUri === null
      ? ''
      : `<img src="${escapeHtml(input.pmsLogoRingDataUri)}" alt="patrickmathieu singularité" />`;
  if (pmsSignature === 'anneau') return ring;
  if (pmsSignature === 'wordmark') return horizontal;
  return `<span>Généré avec</span>${horizontal}`;
}

/**
 * The document header's three flex items — logo, title block, dates — without
 * the `<header>` element itself: the paged document pins that element to the
 * height its tiling reserved, the continuous one lets it size to content.
 */
export function renderDocumentHeaderContent(input: RenderExportHtmlInput): string {
  const logoUri = input.companyLogoDataUri;
  const logo =
    input.dressing.companyLogo && logoUri !== null
      ? `<div class="export-header-logo"><img src="${escapeHtml(logoUri)}" alt="" /></div>`
      : '';
  const title = input.dressing.titleBlock
    ? `<div class="export-header-title"><h1>${escapeHtml(input.matrixTitle)}</h1><p>${escapeHtml(input.companyName)} — Matrice de Survaleur</p></div>`
    : '<div class="export-header-title"></div>';
  const dateLines = [
    input.exportedAt === null ? '' : `<p>Exporté le ${escapeHtml(input.exportedAt)}</p>`,
    input.matrixUpdatedAt === null ? '' : `<p>Version du ${escapeHtml(input.matrixUpdatedAt)}</p>`,
  ].filter((line) => line.length > 0);
  const dates =
    input.dressing.dates && dateLines.length > 0
      ? `<div class="export-header-dates">${dateLines.join('')}</div>`
      : '';
  return `${logo}${title}${dates}`;
}

/**
 * The document header's rules, identical in both layouts — the title keeps
 * its two-line clamp (spec D1/D4) even where the header's height is free.
 * Emitted only when the document has a header (spec R4: a stylesheet
 * describing furniture the page does not have is noise).
 */
export const DOCUMENT_HEADER_CSS = `
  .export-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; box-sizing: border-box; overflow: hidden; border-bottom: 1.5px solid #1f2937; padding-bottom: ${HEADER_VERTICAL_PADDING_PX}px; }
  .export-header h1 { margin: 0; font-size: ${HEADER_TITLE_PT}pt; line-height: ${LINE_HEIGHT_FACTOR}; font-weight: 700; display: -webkit-box; -webkit-line-clamp: ${HEADER_MAX_TITLE_LINES}; -webkit-box-orient: vertical; overflow: hidden; }
  .export-header p { margin: 0; font-size: ${HEADER_META_PT}pt; line-height: ${LINE_HEIGHT_FACTOR}; color: #6b6a67; }
  .export-header-title { flex: 1; min-width: 0; }
  .export-header-dates { text-align: right; white-space: nowrap; }
  .export-header-logo img { display: block; height: ${HEADER_LOGO_HEIGHT_PX}px; width: auto; }`;

/** The `<!doctype html>` shell both layouts wrap their body in. */
export function renderHtmlDocument(title: string, style: string, body: string): string {
  return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${style}</style>
</head>
<body>
${body}
</body>
</html>`;
}
